import React, { useState, memo, useMemo, useRef, useEffect } from "react";
import ThumbnailPdf from "../PdfThumbnail/PdfThumbnail";
import VideoThumbnail from "../VideoThumbnail/VideoThumbnail";
import styles from "./ContentCard.module.scss";
import FrameWrapperRubric from "../frameWrapperRubric/FrameWrapperRubric";
import FrameWrapperArticle from "../framerWrapperArticle/FramerWrapperArticle";
import FrameWrapperPdf from "../frameWrapperPdf/FrameWrapperPdf";
import FrameWrapperVideo from "../frameWrapperVideo/FrameWrapperVideo";
import { Worker } from "@react-pdf-viewer/core";
import { AI_BASE_URL } from "../../config/index";

const PDF_WORKER_URL =
  "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js";

// Helper function to format the source name
const formatSourceName = (sourceName) => {
  if (!sourceName) return "";

  // Split by backslash and take the last part
  if (sourceName.includes("\\")) {
    sourceName = sourceName.split("\\").pop();
  }
  // Split by forward slash in case that's used
  else if (sourceName.includes("/")) {
    sourceName = sourceName.split("/").pop();
  }

  // For long names, truncate at 20 characters and add ellipsis
  if (sourceName.length > 20) {
    sourceName = sourceName.substring(0, 20) + "...";
  }

  return sourceName;
};

// Create a memoized PDF thumbnail component to prevent reloading
const MemoizedPdfThumbnail = memo(
  ({ fileUrl, pageNumber, coverUrl, onError }) => (
    <ThumbnailPdf
      fileUrl={fileUrl}
      pageNumber={pageNumber}
      coverUrl={coverUrl}
      onError={onError}
    />
  ),
  // Custom comparison function
  (prevProps, nextProps) =>
    prevProps.fileUrl === nextProps.fileUrl &&
    prevProps.pageNumber === nextProps.pageNumber
);

const fetchedUrlsCache = new Map();

const convertTimeToVimeoFormat = (timeStr) => {
  if (!timeStr) return "0s";

  // Remove milliseconds by splitting at "."
  const [hms] = timeStr.split(".");

  // Split hours, minutes, seconds
  const parts = hms.split(":").map(Number);
  if (parts.length !== 3) return "0s";

  const [hours, minutes, seconds] = parts;

  let result = "";
  if (hours > 0) result += `${hours}h`;
  if (minutes > 0 || hours > 0) result += `${minutes}m`;
  result += `${seconds}s`;

  return result;
};

const ContentCard = ({
  type,
  question,
  publishDate,
  chain,
  author,
  id,
  rubric,
  url,
  coverUrl,
  name,
  page,
  source,
  sourceTitle,
  sourceUrl,
  target_page,
  extra,
  extra_pages,
  panel,
  token,
  content,
  startTime,
  endTime,
  event,
  userId,
  user,
  userEventsAccess,
  fileObjectKey,
  onError,
}) => {
  const [showIframe, setShowIframe] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const contentRef = useRef(null);
  const [PdfUrl, setPdfUrl] = useState(null);
  const overlayRef = useRef(null);
  const pdfOverlayRef = useRef(null);
  const [silos, setSilos] = useState([]);

  const extractObjectKeyFromUrl = (url) => {
    // Clean the URL from any hash parameters like #t=...
    const cleanedUrl = url ? url.split("#")[0] : null;
    if (!cleanedUrl) return null;
    if (!url) return null;

    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname.substring(1);
      const decodedPath = decodeURIComponent(path);

      return decodedPath;
    } catch (error) {
      console.error("Error extracting object key from URL:", error);
      return null;
    }
  };

  const [hasError, setHasError] = useState(false);

  // Clean the URL from any hash parameters like #t=...
  const cleanedUrl = useMemo(() => {
    return url ? url.split("#")[0] : null;
  }, [url]);

  // useEffect(() => {
  //   const loadSilos = async () => {
  //     if (user && token) {
  //       try {
  //         const fetchedSilos = await getSilos(user, token);
  //         setSilos(fetchedSilos);
  //       } catch (error) {
  //         console.error("Failed to load silos:", error);
  //         setSilos([]);
  //       }
  //     }
  //   };
  //   loadSilos();
  // }, [user?.id, token]);

  const isSpecialDocument = useMemo(
    () =>
      type === "PDF" &&
      silos.some((silo) => {
        const siloName = typeof silo === "string" ? silo : silo.name;
        return siloName === question;
      }),
    [type, question, silos]
  );

  const formattedSourceName = useMemo(
    () => formatSourceName(question),
    [question]
  );

  // Modified cacheKey: Independent of isSpecialDocument for pre-signed URL fetching logic
  const presignCacheKey = useMemo(() => {
    if (type === "PDF" && name) {
      return `presigned_${name}_${page || ""}`; // Ensure key is distinct if used elsewhere
    }
    return null;
  }, [name, type, page]);

  useEffect(() => {
    if (type === "PDF" && name && presignCacheKey && token) {
      // Check if we already have a valid presigned URL
      if (PdfUrl && PdfUrl.includes("X-Amz-Algorithm=")) {
        return;
      }

      // Check cache first
      if (fetchedUrlsCache.has(presignCacheKey)) {
        const cachedUrl = fetchedUrlsCache.get(presignCacheKey);
        if (cachedUrl) {
          setPdfUrl(cachedUrl);
          return;
        }
      }

      let objectKey;
      if (url && url.includes("amazonaws.com")) {
        const extractedKey = extractObjectKeyFromUrl(url);
        objectKey = extractedKey && extractedKey.includes("_extrait_") ? extractedKey : name;
        if (!objectKey) {
          console.error("Could not extract object key from URL:", url);
          setHasError(true);
          return;
        }
      } else {
        if (isSpecialDocument) {
          objectKey = name.replace(/\s/g, "+");
        } else {
          const extractedKey = extractObjectKeyFromUrl(url);
          objectKey = extractedKey && extractedKey.includes("_extrait_") ? extractedKey : fileObjectKey;
        }
      }

      const controller = new AbortController();
      const fetchUrl = `${AI_BASE_URL}/auth/presigned_url?object_key=${encodeURIComponent(
        objectKey
      )}`;

      fetch(fetchUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: "",
        signal: controller.signal,
      })
        .then((response) => {
             if (!response.ok) {
                 // Check if it's a 404 or other error
                 if (response.status === 404) {
                     setHasError(true);
                 }
                 throw new Error("Network response was not ok");
             }
             return response.json();
        })
        .then((data) => {
          if (data.url) {
            setPdfUrl(data.url);
            fetchedUrlsCache.set(presignCacheKey, data.url);
          } else {
            console.error(
              `[ContentCard Presign Failure for NAME: '${name}']: API did not return a URL.`
            );
            setHasError(true);
            if (onError) onError();
          }
        })
        .catch((error) => {
          if (!controller.signal.aborted) {
            console.error(
              `[ContentCard Presign Error for NAME: '${name}']: Error fetching pre-signed URL:`,
              error
            );
            // Hide card on fetch error (likely 404 or perm issue)
            setHasError(true);
            if (onError) onError();
          }
        });

      return () => {
        controller.abort();
      };
    }
  }, [type, name, presignCacheKey, isSpecialDocument, token, PdfUrl]);

  // Custom hook for preventing overlay scroll events
  const useOverlayEventPrevention = (
    elementRef,
    isActive,
    events = ["wheel", "scroll"]
  ) => {
    useEffect(() => {
      const handleOverlayEvent = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };

      const element = elementRef.current;
      if (element && isActive) {
        // Add event listeners for all specified events
        events.forEach((eventType) => {
          element.addEventListener(eventType, handleOverlayEvent, {
            passive: false,
          });
        });

        // Cleanup function
        return () => {
          events.forEach((eventType) => {
            element.removeEventListener(eventType, handleOverlayEvent);
          });
        };
      }
    }, [elementRef, isActive, events]);
  };

  // Apply overlay event prevention to both overlays
  useOverlayEventPrevention(overlayRef, showIframe);
  useOverlayEventPrevention(pdfOverlayRef, showPdfViewer);

  const formattedDate = useMemo(() => {
    if (!publishDate) return "";
    return new Date(publishDate).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [publishDate]);

  const getClassName = (type) => {
    return type === "RUBRIC"
      ? styles.cardRubric
      : type === "ARTICLE"
      ? styles.cardArticle
      : type === "VIDEO"
      ? styles.cardVideo
      : styles.cardPdf;
  };

  const openPopup = () => {
    setShowIframe(true);
  };

  const closePopup = () => {
    setShowIframe(false);
  };

  const openPdfViewer = () => {
    setShowPdfViewer(true);
  };

  const closePdfViewer = () => {
    setShowPdfViewer(false);
  };

  const authorsWithAvatars = useMemo(() => {
    if (!author) return null;
    return Array.isArray(author) ? author.filter((a) => a.enableAvatar) : [];
  }, [author]);
  const primaryPage = target_page || (!isSpecialDocument && page) || null;

  // Combine primaryPage and extra pages
  const combinedExtra = [
    ...(extra || []),
    ...(extra_pages || []).map((page) => page.original_page).filter((p) => p != null && !isNaN(p)),
  ];
  const allPages = [primaryPage, ...combinedExtra].filter((p) => p != null && !isNaN(Number(p))).map(Number); // Remove null/undefined/NaN and convert to numbers

  // Memoize PDF render content
  const pdfContent = useMemo(() => {
    if (type !== "PDF") return null;

    // Determine the primary page to display
    const minPage = allPages.length > 0 ? Math.min(...allPages) : null;
    const maxPage = allPages.length > 0 ? Math.max(...allPages) : null;

    let pageDisplay;
    if (allPages.length === 0) {
      pageDisplay = null; // No page info available
    } else if (allPages.length === 1 || minPage === maxPage) {
      pageDisplay = `Page ${minPage}`;
    } else {
      pageDisplay = `Pages ${minPage} - ${maxPage}`;
    }

    return (
      <>
        <div className={`${styles.titlePdf} ${panel ? styles.panelOpen : ""}`}>
          <p>
            {formattedSourceName}
            {pageDisplay && (
              <>
                <br />
                <span className={styles.cardPublishDate}>{pageDisplay}</span>
              </>
            )}
          </p>
        </div>
        <MemoizedPdfThumbnail
          fileUrl={PdfUrl}
          pageNumber={isSpecialDocument ? 3 : primaryPage}
          coverUrl={coverUrl}
          onError={() => {
            setHasError(true);
            if (onError) onError();
          }}
        />
      </>
    );
  }, [
    type,
    formattedSourceName,
    PdfUrl,
    isSpecialDocument,
    coverUrl,
    primaryPage,
    allPages,
    panel,
  ]);

  if (hasError) return null;

  return (
    <div className={styles.container} ref={contentRef}>
      <div
        className={getClassName(type)}
        onClick={type === "PDF" ? openPdfViewer : null}
      >
        <div
          onClick={type === "PDF" ? openPdfViewer : openPopup}
          className={styles.cardHeader}
        >
          {type !== "VIDEO" && <span className={styles.cardType}>{type}</span>}
        </div>

        <div className={styles.cardBody}>
          {type === "VIDEO" && (
            <div className={styles.cardVideo} onClick={openPopup}>
              <div className={styles.videoThumb}>
                <VideoThumbnail url={cleanedUrl} thumbnailSize="large" />
              </div>
            </div>
          )}

          {type !== "PDF" && type !== "VIDEO" ? (
            <>
              <h4
                onClick={type === "PDF" ? openPdfViewer : openPopup}
                className={styles.cardQuestion}
              >
                {question}
              </h4>

              {type === "RUBRIC" && sourceTitle && sourceUrl && (
                <>
                  <div
                    className={styles.cardHeader}
                    onClick={() => window.open(sourceUrl, "_blank")}
                  >
                    <span className={styles.cardSource}>Source</span>
                  </div>
                  <p
                    onClick={() => window.open(sourceUrl, "_blank")}
                    className={styles.cardPublishDate}
                  >
                    {sourceUrl}
                  </p>
                </>
              )}
              <p className={styles.cardPublishDate}>
                publié le : {formattedDate}
              </p>
            </>
          ) : (
            pdfContent
          )}
          <div className={styles.cardFooter}>
            {chain && (
              <img
                className={styles.cardLogo}
                src={chain[0].mediaChain[0].avatarUrl}
                alt={`Logo de la chaîne ${chain[0].mediaChain[0].name}`}
              />
            )}
            {authorsWithAvatars && (
              <>
                {authorsWithAvatars.length > 0 &&
                  authorsWithAvatars.map((author, index) => (
                    <div
                      className={styles.cardAuthorContainer}
                      key={`author-${author.id || index}`}
                    >
                      <img
                        className={styles.cardAuthor}
                        src={author.avatarUrl}
                        alt={`Auteur ${author.name}`}
                      />
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      </div>
      {showIframe && (
        <div
          ref={overlayRef}
          className={styles.overlay}
          onClick={(e) => {
            // Only close if clicking directly on the overlay (not on children)
            if (e.target === e.currentTarget) {
              closePopup();
            }
          }}
        >
          {type === "ARTICLE" && (
            <FrameWrapperArticle
              id={id}
              closePopup={closePopup}
              token={token}
            />
          )}
          {type === "RUBRIC" && (
            <FrameWrapperRubric
              rubric={rubric}
              type={type}
              closePopup={closePopup}
            />
          )}
          {type === "VIDEO" && (
            <FrameWrapperVideo
              url={cleanedUrl}
              startTime={startTime}
              endTime={endTime}
              closePopup={closePopup}
              user={user}
              token={token}
              event={event}
              userEventsAccess={userEventsAccess}
            />
          )}
        </div>
      )}
      {showPdfViewer && (
        <div className={styles.overlay}>
          <Worker workerUrl={PDF_WORKER_URL}>
            <div
              style={{
                border: "1px solid rgba(0, 0, 0, 0.3)",
                height: "90%",
                width: "70%",
                margin: "0 auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FrameWrapperPdf
                closePopup={closePdfViewer}
                PdfUrl={PdfUrl}
                page={page}
                isSpecialDocument={isSpecialDocument}
                highlited_pages={extra_pages}
                content={content}
              />
            </div>
          </Worker>
        </div>
      )}
    </div>
  );
};

export default memo(ContentCard, (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.question === nextProps.question &&
    prevProps.url === nextProps.url &&
    prevProps.page === nextProps.page &&
    prevProps.id === nextProps.id &&
    prevProps.name === nextProps.name &&
    prevProps.extra === nextProps.extra &&
    prevProps.extra_pages === nextProps.extra_pages &&
    prevProps.panel === nextProps.panel &&
    prevProps.token === nextProps.token &&
    prevProps.content === nextProps.content &&
    prevProps.user === nextProps.user
  );
});
